import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";

export interface MonthCalendarProps {
  selected?: Dayjs;
  onSelect: (date: Dayjs) => void;
  /** Disable (grey out) days after today — useful for dates of birth. */
  disableFuture?: boolean;
  className?: string;
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * A lightweight, dependency-free month-grid calendar built on Day.js.
 * Provides month (‹ ›) and year (« ») navigation and returns a Dayjs on select.
 */
export const MonthCalendar: React.FC<MonthCalendarProps> = ({
  selected,
  onSelect,
  disableFuture = false,
  className,
}) => {
  const today = dayjs();
  const [view, setView] = React.useState<Dayjs>(
    (selected ?? today).startOf("month")
  );

  // Re-center on the selected date if it changes from outside.
  React.useEffect(() => {
    if (selected) setView(selected.startOf("month"));
  }, [selected]);

  const startWeekday = view.startOf("month").day(); // 0 (Sun) – 6 (Sat)
  const daysInMonth = view.daysInMonth();

  const cells: (Dayjs | null)[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(view.date(d));

  // Year dropdown range: current year back to 1900 (covers any date of birth).
  const years = Array.from(
    { length: today.year() - 1900 + 1 },
    (_, i) => today.year() - i
  );
  const selectClass =
    "rounded-md border bg-background px-1.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className={cn("w-[17rem] select-none p-1", className)}>
      <div className="flex items-center justify-between gap-1 px-1 pb-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Previous month"
          onClick={() => setView(view.subtract(1, "month"))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-1">
          <select
            aria-label="Month"
            className={selectClass}
            value={view.month()}
            onChange={(e) => setView(view.month(Number(e.target.value)))}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i}>
                {m}
              </option>
            ))}
          </select>
          <select
            aria-label="Year"
            className={selectClass}
            value={view.year()}
            onChange={(e) => setView(view.year(Number(e.target.value)))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Next month"
          onClick={() => setView(view.add(1, "month"))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="text-center text-xs font-medium text-muted-foreground"
          >
            {w}
          </div>
        ))}
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const isSelected = selected ? day.isSame(selected, "day") : false;
          const isToday = day.isSame(today, "day");
          const disabled = disableFuture && day.isAfter(today, "day");
          return (
            <button
              key={day.format("YYYY-MM-DD")}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(day)}
              className={cn(
                "h-8 w-8 rounded-md text-sm transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                isSelected &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                !isSelected && isToday && "border border-primary/50",
                disabled && "pointer-events-none opacity-40"
              )}
            >
              {day.date()}
            </button>
          );
        })}
      </div>
    </div>
  );
};
